import _ from 'lodash';
import apiDuck /* , { PROCESSING_FLAG } */ from 'shared/lib/apiDuck';
import { upsertEntity } from 'shared/lib/normalizer';
import { JOB_OFFER_STATUS, isJobOfferInactive } from 'shared/constants';

const {
  createReducer,
  createLoadEntityActions,
  createProcessEntityActions,
  getEntities,
  isLoading,
  isLoadingEntity,
  isProcessingEntity,
  hasEntityError
} = apiDuck('offers');
const [LOAD_DETAILS, LOADING_DETAILS, LOADED_DETAILS, LOAD_DETAILS_FAILED] = createLoadEntityActions('LOAD_DETAILS');
const [ADD_CHANGE, ADDING_CHANGE, ADDED_CHANGE] = createProcessEntityActions('ADD_CHANGE');
const MARK_READ = 'OFFER_MARK_READ';

const reducer = createReducer((state, action) => {
  switch (action.type) {
    case LOADED_DETAILS: {
      return upsertEntity(state, action.result, action.payload.id);
    }

    case 'JOB_OFFER_ADDED@UPDATE': {
      return upsertEntity(state, action.payload, action.payload.id, true);
    }

    case MARK_READ:
    case 'JOB_OFFER_CHANGES_READ@UPDATE': {
      const { offerId, changes = {}, readAt } = action.payload;

      if (!state.entities[offerId]) {
        return state;
      }

      return {
        ...state,
        entities: {
          ...state.entities,
          [offerId]: {
            ...state.entities[offerId],
            changes: state.entities[offerId].changes.map(change => ({
              ...change,
              readAt: changes[change.id] || readAt || change.readAt
            })),
            changesStats: {
              ...state.entities[offerId].changesStats,
              unread: 0,
            }
          }
        }
      };
    }

    case 'JOB_OFFER_CHANGE@UPDATE':
    case ADDED_CHANGE: {
      const data = action.type === ADDED_CHANGE ? action.result : action.payload;
      const { jobOfferId } = data;

      // job offer does not exist or malformed id
      if (!(jobOfferId && state.entities[jobOfferId])) return state;

      const entity = state.entities[jobOfferId];
      const entityChanges = entity.changes || [];

      // change already exists
      if (entityChanges.find(ch => ch.id === data.change.id)) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [jobOfferId]: {
            ...entity,
            statusId: data.statusId || entity.statusId,
            readAt: data.change.statusId === JOB_OFFER_STATUS.READ ? data.change.readAt : entity.readAt,
            jobApplicationId: data.jobApplicationId || entity.jobApplicationId,
            rejectionReasonId: data.jobApplicationId || entity.rejectionReasonId,
            changes: entityChanges.length > 0
              ? [data.change, ...entityChanges]
              : [],
            changesStats: {
              unread: entity.changesStats.unread + (data.change.message && data.change.createdByCompanyId ? 1 : 0),
              total: entity.changesStats.total + 1,
              last: data.change.createdAt
            }
          }
        }
      };
    }

    default:
      return state;
  }
});

export default reducer;

export const actions = {
  loadDetails(offerId) {
    return {
      type: LOAD_DETAILS,
      types: [LOADING_DETAILS, LOADED_DETAILS, LOAD_DETAILS_FAILED],
      callApi: client => client.get(`/user/jobs/offers/${offerId}`),
      payload: {
        id: offerId
      },
    };
  },

  addChange(jobId, offerId, data, onSuccess, onFailure) {
    return {
      type: ADD_CHANGE,
      types: [ADDING_CHANGE, ADDED_CHANGE],
      callApi: client => client.post(`/user/jobs/${jobId}/offers/${offerId}`, data),
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : [],
      payload: {
        id: offerId
      }
    };
  },

  markChangesRead(offerId) {
    return {
      type: MARK_READ,
      payload: {
        offerId,
        readAt: new Date().toISOString()
      },
    };
  },
};

export const selectors = {
  isLoading,
  isLoadingEntity,
  isProcessingEntity,
  hasEntityError,

  getAllOffers(state) {
    return getEntities(state).entities;
  },

  getOffer(state, offerId) {
    return state.offers.entities[offerId];
  },

  getUnreadOfferChangesCount(state) {
    return _.values(state.offers.entities).reduce((acc, curr) => acc + (curr && curr.changesStats ? curr.changesStats.unread : 0), 0);
  },

  getActiveOfferByJobId(state, jobId) {
    if (!jobId) {
      return null;
    }

    return getEntities(state, entity => entity.job.id === jobId && !isJobOfferInactive(entity.statusId)).entities[0] || null;
  },
};
